import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Flex, Text } from '@mantine/core';
import { Textarea, TextInput } from 'react-hook-form-mantine';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type { WitnessDetails } from './usePublicSigningInfo';

interface Props {
	defaultValues: WitnessDetails | null;
	isSubmitting: boolean;
	hasError: boolean;
	onSubmit: (details: WitnessDetails) => void;
	/** When present, shows a "back to signing" action (only once details already exist). */
	onCancel?: () => void;
	/** DC-825: a KYC certifier gives a professional capacity where a witness gives an occupation. */
	isCertifier?: boolean;
}

/**
 * Step shown to a witness or KYC certifier before signing. Collects the full name, address and
 * occupation (a certifier's professional capacity) stamped onto the executed document. All three
 * fields are mandatory.
 */
export const WitnessDetailsStep = ({
	defaultValues,
	isSubmitting,
	hasError,
	onSubmit,
	onCancel,
	isCertifier = false,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const occupationRequired = isCertifier
		? tCommonUi('eid_easy_signing.certification.details.capacity_required', {
				defaultValue: 'Professional capacity is required',
			})
		: tCommonUi('eid_easy_signing.witness_details.validation.occupation', {
				defaultValue: 'Occupation is required',
			});

	const form = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: zodResolver(
			z.object({
				fullName: z
					.string()
					.trim()
					.min(1, {
						message: tCommonUi(
							'eid_easy_signing.witness_details.validation.full_name',
							{
								defaultValue: 'Full name is required',
							}
						),
					})
					.max(200),
				address: z
					.string()
					.trim()
					.min(1, {
						message: tCommonUi('eid_easy_signing.witness_details.validation.address', {
							defaultValue: 'Address is required',
						}),
					})
					.max(500),
				occupation: z.string().trim().min(1, { message: occupationRequired }).max(200),
			})
		),
		defaultValues: {
			fullName: defaultValues?.fullName ?? '',
			address: defaultValues?.address ?? '',
			occupation: defaultValues?.occupation ?? '',
		},
	});
	const { control, handleSubmit, formState } = form;

	return (
		<FormProvider {...form}>
			<form
				data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_WITNESS_DETAILS_FORM}
				onSubmit={handleSubmit((values) =>
					onSubmit({
						fullName: values.fullName.trim(),
						address: values.address.trim(),
						occupation: values.occupation.trim(),
					})
				)}
			>
				<Flex direction="column" gap="1rem" p="1.5rem">
					<Box>
						<Text fw={600} size="lg">
							{isCertifier
								? tCommonUi('eid_easy_signing.certification.details.title', {
										defaultValue: 'Your details as certifier',
									})
								: tCommonUi('eid_easy_signing.witness_details.title', {
										defaultValue: 'Your details as witness',
									})}
						</Text>
						<Text size="sm" c="dimmed">
							{isCertifier
								? tCommonUi('eid_easy_signing.certification.details.subtitle', {
										defaultValue:
											'These details appear next to your signature on the certified copy.',
									})
								: tCommonUi('eid_easy_signing.witness_details.subtitle', {
										defaultValue:
											'These details are required by law and will appear on the signed document.',
									})}
						</Text>
					</Box>

					<TextInput
						name="fullName"
						control={control}
						size="md"
						label={tCommonUi('eid_easy_signing.witness_details.full_name_label', {
							defaultValue: 'Full name',
						})}
						placeholder={tCommonUi(
							'eid_easy_signing.witness_details.full_name_placeholder',
							{ defaultValue: 'e.g. Jane Smith' }
						)}
						data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_WITNESS_FULL_NAME}
					/>
					<Textarea
						name="address"
						control={control}
						size="md"
						minRows={2}
						autosize
						label={tCommonUi('eid_easy_signing.witness_details.address_label', {
							defaultValue: 'Address',
						})}
						placeholder={tCommonUi(
							'eid_easy_signing.witness_details.address_placeholder',
							{
								defaultValue: 'Street, city, postcode, country',
							}
						)}
						data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_WITNESS_ADDRESS}
					/>
					<TextInput
						name="occupation"
						control={control}
						size="md"
						label={
							isCertifier
								? tCommonUi(
										'eid_easy_signing.certification.details.capacity_label',
										{
											defaultValue: 'Professional capacity',
										}
									)
								: tCommonUi('eid_easy_signing.witness_details.occupation_label', {
										defaultValue: 'Occupation',
									})
						}
						placeholder={
							isCertifier
								? tCommonUi(
										'eid_easy_signing.certification.details.capacity_placeholder',
										{ defaultValue: 'e.g. Solicitor, SRA number 123456' }
									)
								: tCommonUi(
										'eid_easy_signing.witness_details.occupation_placeholder',
										{
											defaultValue: 'e.g. Solicitor',
										}
									)
						}
						data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_WITNESS_OCCUPATION}
					/>

					{hasError && (
						<Text c="red" size="sm">
							{tCommonUi('eid_easy_signing.witness_details.save_error', {
								defaultValue:
									'Something went wrong saving your details. Please try again.',
							})}
						</Text>
					)}

					<Button
						type="submit"
						fullWidth
						variant="primary"
						loading={isSubmitting}
						disabled={!formState.isValid || isSubmitting}
						data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_WITNESS_DETAILS_SUBMIT}
					>
						{isCertifier
							? tCommonUi('eid_easy_signing.certification.details.submit', {
									defaultValue: 'Continue',
								})
							: tCommonUi('eid_easy_signing.witness_details.submit', {
									defaultValue: 'Continue to sign',
								})}
					</Button>

					{onCancel && (
						<Button
							type="button"
							fullWidth
							variant="subtle"
							onClick={onCancel}
							data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_WITNESS_DETAILS_CANCEL}
						>
							{tCommonUi('eid_easy_signing.witness_details.cancel', {
								defaultValue: 'Back to signing',
							})}
						</Button>
					)}
				</Flex>
			</form>
		</FormProvider>
	);
};
